import React from 'react';

interface RadarChartProps {
  data: Array<{
    category: string;
    score: number;
    maxScore?: number;
  }>;
  size?: number;
  className?: string;
}

export const RadarChart: React.FC<RadarChartProps> = ({ 
  data, 
  size = 300, 
  className = '' 
}) => {
  const maxScore = 5;
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = size * 0.35; // Chart radius

  // Calculate positions for each category (5 categories = 72 degrees apart)
  const getCategoryPosition = (index: number, score: number) => {
    const angle = (index * 72 - 90) * (Math.PI / 180); // Start from top, clockwise
    const distance = (score / maxScore) * radius;
    const x = centerX + Math.cos(angle) * distance;
    const y = centerY + Math.sin(angle) * distance;
    return { x, y };
  };

  // Generate grid circles
  const gridCircles = [];
  for (let i = 1; i <= 5; i++) {
    const gridRadius = (i / 5) * radius;
    gridCircles.push(
      <circle
        key={i}
        cx={centerX}
        cy={centerY}
        r={gridRadius}
        fill="none"
        stroke="#e5e7eb"
        strokeWidth="1"
        strokeDasharray="2,2"
      />
    );
  }

  // Generate axis lines
  const axisLines = [];
  for (let i = 0; i < 5; i++) {
    const angle = (i * 72 - 90) * (Math.PI / 180);
    const x = centerX + Math.cos(angle) * radius;
    const y = centerY + Math.sin(angle) * radius;
    axisLines.push(
      <line
        key={i}
        x1={centerX}
        y1={centerY}
        x2={x}
        y2={y}
        stroke="#e5e7eb"
        strokeWidth="1"
      />
    );
  }

  // Generate data polygon
  const polygonPoints = data
    .map((item, index) => getCategoryPosition(index, item.score))
    .map(({ x, y }) => `${x},${y}`)
    .join(' ');

  // Generate data points
  const dataPoints = data.map((item, index) => {
    const { x, y } = getCategoryPosition(index, item.score);
    return (
      <circle
        key={index}
        cx={x}
        cy={y}
        r="4"
        fill="#7c3aed"
        stroke="white"
        strokeWidth="2"
      />
    );
  });

  // Generate category labels
  const categoryLabels = data.map((item, index) => {
    const angle = (index * 72 - 90) * (Math.PI / 180);
    const labelRadius = radius + 30; // Position labels outside the chart
    const x = centerX + Math.cos(angle) * labelRadius;
    const y = centerY + Math.sin(angle) * labelRadius;
    
    return (
      <text
        key={index}
        x={x}
        y={y}
        textAnchor="middle"
        dominantBaseline="middle"
        className="text-xs font-medium text-gray-700 fill-current"
        style={{
          fontSize: '11px',
          fontWeight: '500'
        }}
      >
        {item.category.split(' ').map((word, i) => (
          <tspan key={i} x={x} dy={i === 0 ? 0 : '12'}>
            {word}
          </tspan>
        ))}
      </text>
    );
  });

  // Generate score labels
  const scoreLabels = [];
  for (let i = 1; i <= 5; i++) {
    const gridRadius = (i / 5) * radius;
    const x = centerX + 5;
    const y = centerY - gridRadius + 4;
    scoreLabels.push(
      <text
        key={i}
        x={x}
        y={y}
        textAnchor="start"
        dominantBaseline="middle"
        className="text-xs text-gray-500 fill-current"
        style={{ fontSize: '10px' }}
      >
        {i === 5 ? '5.0' : i.toString()}
      </text>
    );
  }

  return (
    <div className={`flex flex-col items-center ${className}`}>
      <svg width={size} height={size} className="mb-4">
        {/* Grid circles */}
        {gridCircles}
        
        {/* Axis lines */}
        {axisLines}
        
        {/* Score labels */}
        {scoreLabels}
        
        {/* Data polygon */}
        <polygon
          points={polygonPoints}
          fill="rgba(124, 58, 237, 0.2)"
          stroke="#7c3aed"
          strokeWidth="2"
        />
        
        {/* Data points */}
        {dataPoints}
        
        {/* Category labels */}
        {categoryLabels}
      </svg>
      
      {/* Legend */}
      <div className="flex items-center space-x-2">
        <div className="w-3 h-3 bg-purple-600 rounded"></div>
        <span className="text-sm text-gray-600">Jouw Score</span>
      </div>
    </div>
  );
};
